import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';

import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable, Subscription, firstValueFrom, interval, shareReplay, tap } from 'rxjs';

import { logout, setAccessToken, setRefreshToken } from '@lib/auth/store';
import { selectAccessToken, selectDsToken, selectRefreshToken } from '@lib/auth/store';

import { setUser } from '@lib/core/stores/user/user.actions';
import { AUTH_API_URL } from '@lib/core/tokens/api.tokens';

import {
  AuthenticationResponseModel,
  DsCredentialsRequestBody,
  DsCredentialsResponseBody,
  DsKeyValidationRequestBody,
  DsKeyValidationResponseBody,
  DsSignResponseBody,
  LiloLoginWithHash,
} from '../models/auth-response.interface';
import { AuthState } from '../models/auth-state';
import { AuthType } from '../models/auth-type.enum';

const DEPOSIT_SIGN_API_ROOT = 'https://depositsign.com/api/v1/do-2bd4419b-7d09-4ba5-b849-94e11ddc42a9';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly store = inject(Store<AuthState>);

  private readonly authApiUrl: string;
  private readonly authType = AuthType;

  accessToken = this.store.selectSignal<string | null>(selectAccessToken);
  refreshToken = this.store.selectSignal<string | null>(selectRefreshToken);
  depositSignToken = this.store.selectSignal<string | null>(selectDsToken);
  type = signal<AuthType>(this.authType.Otp);

  private readonly accessTokenExpirationDate = computed<Date | null>(() => {
    if (!this.accessToken()) return null;
    const payload = this.decodeJwtPayload(this.accessToken()!);

    return payload?.exp ? new Date(payload.exp * 1000 - 300000) : null;
  });
  private readonly refreshTokenExpirationDate = computed<Date | null>(() => {
    if (!this.refreshToken()) return null;
    const payload = this.decodeJwtPayload(this.refreshToken()!);

    return payload?.exp ? new Date(payload.exp * 1000) : null;
  });

  dsSignResult$ = new BehaviorSubject<boolean>(false);
  successAuthResult$ = new BehaviorSubject<boolean>(false);

  private authTimer$: Subscription | null = null;

  constructor(@Inject(AUTH_API_URL) authApiUrl: string) {
    this.authApiUrl = authApiUrl;
  }

  private decodeJwtPayload(token: string) {
    try {
      const base64Url = token.split('.')[1];
      if (!base64Url) throw new Error('Invalid JWT token format');

      let base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const padding = base64.length % 4;
      if (padding) base64 += '='.repeat(4 - padding);

      const binaryString = this.base64ToBinary(base64);
      const jsonPayload = decodeURIComponent(
        Array.from(binaryString)
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );

      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Error decoding JWT token:', error);
      return null;
    }
  }

  private base64ToBinary(base64: string): string {
    try {
      return atob(base64);
    } catch {
      const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
      let result = '';
      let i = 0;

      while (i < base64.length) {
        const encoded1 = chars.indexOf(base64[i++]);
        const encoded2 = chars.indexOf(base64[i++]);
        const encoded3 = chars.indexOf(base64[i++]);
        const encoded4 = chars.indexOf(base64[i++]);

        const bitmap = (encoded1 << 18) | (encoded2 << 12) | (encoded3 << 6) | encoded4;

        result += String.fromCharCode((bitmap >> 16) & 255);
        if (encoded3 !== 64) result += String.fromCharCode((bitmap >> 8) & 255);
        if (encoded4 !== 64) result += String.fromCharCode(bitmap & 255);
      }

      return result;
    }
  }

  private extractAndStoreUserFromToken(token: string) {
    try {
      const payload = this.decodeJwtPayload(token);
      if (payload) {
        // Extract user information from JWT payload matching User interface
        const user = {
          id: payload.sub || payload.userId || '',
          userId: payload.sub || payload.userId || '',
          email: payload.email || '',
          phoneCode: payload.phoneCode || '',
          phoneNumber: payload.phoneNumber || payload.phone || '',
          gender: payload.gender || '',
          birthDate: payload.birthDate || '',
          firstName: payload.firstName || payload.given_name || '',
          lastName: payload.lastName || payload.family_name || '',
          middleName: payload.middleName || '',
          fullName: payload.name || payload.fullName || `${payload.firstName || ''} ${payload.lastName || ''}`.trim(),
        };

        this.store.dispatch(setUser({ user }));
      }
    } catch (error) {
      console.error('Error extracting user from JWT token:', error);
    }
  }

  isTokenExpired(token: string | null): boolean {
    if (!token) return true;

    const payload = this.decodeJwtPayload(token);
    if (!payload || !payload.exp) return true;

    const expirationTime = payload.exp * 1000; // Convert to milliseconds
    const currentTime = Date.now();
    const bufferTime = 60 * 1000; // 1 minute buffer before expiration
    return currentTime >= expirationTime + bufferTime;
  }

  async requestOtp(phoneNumber: string) {
    try {
      await firstValueFrom(
        this.http.post<AuthenticationResponseModel>(`${this.authApiUrl}/login/otp`, {
          phoneNumber: phoneNumber,
        })
      );
      return true;
    } catch {
      return false;
    }
  }

  async requestViber(phoneNumber: string) {
    try {
      await firstValueFrom(
        this.http.post<AuthenticationResponseModel>(`${this.authApiUrl}/login/otp/viber`, {
          phoneNumber: phoneNumber,
        })
      );
      return true;
    } catch {
      return false;
    }
  }

  async loginWithPassword(phoneNumber: string, password: string): Promise<boolean> {
    try {
      const tokenResult = await firstValueFrom(
        this.http.post<AuthenticationResponseModel>(`${this.authApiUrl}/login`, {
          phoneNumber,
          password,
        })
      );

      if (tokenResult.accessToken) {
        this.store.dispatch(setAccessToken({ accessToken: tokenResult.accessToken }));
        this.store.dispatch(setRefreshToken({ refreshToken: tokenResult.refreshToken }));
        this.extractAndStoreUserFromToken(tokenResult.accessToken);
        return true;
      } else {
        this.logout();
      }
    } catch (e) {
      console.error('Login error: ', e);
    }
    return false;
  }

  async verifyCode(phoneNumber: string, code: string): Promise<boolean> {
    try {
      const tokenResult = await firstValueFrom(
        this.http.post<AuthenticationResponseModel>(`${this.authApiUrl}/login/otp/verify`, {
          phoneNumber,
          code,
        })
      );
      if (tokenResult.accessToken) {
        this.store.dispatch(setAccessToken({ accessToken: tokenResult.accessToken }));
        this.store.dispatch(setRefreshToken({ refreshToken: tokenResult.refreshToken }));
        this.extractAndStoreUserFromToken(tokenResult.accessToken);
        return true;
      } else {
        this.logout();
      }
    } catch (e) {
      console.error('Error with otp verification: ', e);
    }
    return false;
  }

  postRefreshToken(refreshToken: string): Observable<AuthenticationResponseModel> {
    return this.http.post<AuthenticationResponseModel>(`${this.authApiUrl}/login/refresh`, { refreshToken: refreshToken });
  }

  postDsCredentials(requestBody: DsCredentialsRequestBody): Observable<DsCredentialsResponseBody> {
    return this.http.post<DsCredentialsResponseBody>(`${DEPOSIT_SIGN_API_ROOT}/auth/login`, requestBody);
  }

  postDsKeyValidation(requestBody: DsKeyValidationRequestBody): Observable<DsKeyValidationResponseBody> {
    return this.http.post<DsKeyValidationResponseBody>(`${DEPOSIT_SIGN_API_ROOT}/keys/${requestBody.KeyName}/password/validate`, requestBody);
  }

  getHashForDs(loginWithCerts: { login: string; certs?: string[] }): Observable<string> {
    return this.http.post<string>(`${this.authApiUrl}/prelogin`, loginWithCerts);
  }

  signHash(dsSignRequest: object): Observable<DsSignResponseBody> {
    return this.http.post<DsSignResponseBody>(`${DEPOSIT_SIGN_API_ROOT}/sign/hash/cades`, dsSignRequest);
  }

  loginWithDs(loginCredentials: LiloLoginWithHash): Observable<AuthenticationResponseModel> {
    return this.http.post<AuthenticationResponseModel>(`${this.authApiUrl}/login/key`, loginCredentials);
  }

  logout() {
    this.stopAuthTimer();
    this.store.dispatch(logout());
    this.router.navigate(['/login']).then();
  }

  startAuthTimer() {
    this.stopAuthTimer();
    this.handleTokenExpiration();
    this.authTimer$ = interval(300000).subscribe(() => {
      this.handleTokenExpiration();
    });
  }

  stopAuthTimer() {
    if (this.authTimer$) {
      this.authTimer$.unsubscribe();
      this.authTimer$ = null;
    }
  }

  handleTokenExpiration() {
    const now = new Date();
    const accessExpiration = this.accessTokenExpirationDate();
    const refreshExpiration = this.refreshTokenExpirationDate();

    if (!accessExpiration && !refreshExpiration) return;
    if (refreshExpiration && refreshExpiration < now) {
      this.logout();
      return;
    }
    if (accessExpiration && accessExpiration < now) {
      this.postRefreshToken(this.refreshToken()!)
        .pipe(
          tap((refreshResponse: AuthenticationResponseModel) => {
            this.store.dispatch(setAccessToken({ accessToken: refreshResponse.accessToken }));
            this.store.dispatch(setRefreshToken({ refreshToken: refreshResponse.refreshToken }));
          }),
          shareReplay(1)
        )
        .subscribe();
    }
  }

  initializeTokens() {
    const localAccessToken = localStorage.getItem('accessToken');
    const localRefreshToken = localStorage.getItem('refreshToken');

    if (localAccessToken && localRefreshToken) {
      if (!this.isTokenExpired(localAccessToken)) {
        this.store.dispatch(setAccessToken({ accessToken: localAccessToken }));
        this.store.dispatch(setRefreshToken({ refreshToken: localRefreshToken }));
        this.startAuthTimer();
      } else {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
      }
    }
  }
}
